import type { FitAnalysisResult } from '@travel-planner/shared/schemas';
import type { GeneratorState } from './use-agent-panel-store';
import { skipToken } from '@tanstack/react-query';
import { useCallback } from 'react';
import { trpc } from '@/shared/api/trpc';
import { useAgentPanelStore } from './use-agent-panel-store';

export type { FitAnalysisResult };

// Subset of GeneratorState that carries the analysis inputs.
type GeneratorStateWithInputs =
  | (GeneratorState & { phase: 'options' })
  | (GeneratorState & { phase: 'preview' | 'budget' });

function getAnalysisInputs(
  gs: GeneratorState | null
): GeneratorStateWithInputs | null {
  if (!gs) return null;
  if (
    gs.phase === 'options' ||
    gs.phase === 'preview' ||
    gs.phase === 'budget'
  ) {
    return gs as GeneratorStateWithInputs;
  }
  return null;
}

/**
 * Reads the recommendation analysis for the active generator brief.
 *
 * The analysis is server state — a pure function of its inputs — so it lives
 * in the tRPC/TanStack Query cache, keyed by those inputs, not in Zustand.
 * The query cache survives panel close/reopen, tab switches, and component
 * unmount; a new analysis only runs when the inputs (extracted / options /
 * preselected / conversationContext) materially change. Equal inputs hash to
 * the same query key, so the cached result is returned without a re-fetch.
 */
export function useFitAnalysis(
  itineraryId: string | null
): FitAnalysisResult | null {
  const generatorState = useAgentPanelStore((s) => s.generatorState);
  const inputs = getAnalysisInputs(generatorState);
  const phase = inputs?.phase ?? null;
  const isAnalysisPhase =
    phase === 'options' || phase === 'preview' || phase === 'budget';

  const query = trpc.agentPanel.recommendationAnalysis.useQuery(
    isAnalysisPhase && itineraryId && inputs
      ? {
          itineraryId,
          extracted: inputs.extracted,
          options: inputs.options,
          preselected: inputs.preselected,
          // Replay the conversation captured when the generator opened — the
          // backend uses it to extract per-day intent and to count emails /
          // chat messages on the source card. Without this, the analysis
          // sees only the structured brief and reports zeros.
          conversationContext:
            inputs.recommendation.conversationContext ?? undefined,
        }
      : skipToken,
    {
      // The analysis is deterministic for a given input set — never let the
      // query go stale and refetch on its own; only an input change (a new
      // query key) should trigger a fresh run.
      staleTime: Number.POSITIVE_INFINITY,
      gcTime: Number.POSITIVE_INFINITY,
    }
  );

  return query.data ?? null;
}

export function useResuggestForDay(itineraryId: string | null) {
  const setDayOverride = useAgentPanelStore((s) => s.setDayOverride);
  const mutation = trpc.agentPanel.resuggestForDay.useMutation();

  const resuggest = useCallback(
    async (args: {
      dayIndex: number;
      intent: string;
      currentSelections: {
        activityIds: string[];
        accommodationId: string | null;
        diningIds: string[];
        transportationIds: string[];
      };
    }) => {
      if (!itineraryId) return [];
      const result = await mutation.mutateAsync({
        itineraryId,
        ...args,
      });
      return result;
    },
    [itineraryId, mutation]
  );

  const applyResuggestion = useCallback(
    (
      dayIndex: number,
      category: 'activityIds' | 'diningIds' | 'transportationIds',
      productIds: string[]
    ) => {
      setDayOverride(dayIndex, { [category]: productIds });
    },
    [setDayOverride]
  );

  return { resuggest, applyResuggestion, isLoading: mutation.isPending };
}
